import { useState } from "react";
import { CreditCard, Loader2, ShieldCheck, Wallet } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, feeOf, fmtBtc, fmtUsd, MIN_FEE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const AMOUNTS = [10, 50, 100, 250, 500, 1000];
const MIN_CARD = 0.5, MAX = 10000;

const MethodToggle = ({ method, setMethod, usdBal }) => (
  <div className="grid grid-cols-2 gap-2">
    {[{ k: "card", l: "Credit card", I: CreditCard }, { k: "balance", l: `USD balance · ${fmtUsd(usdBal)}`, I: Wallet }].map(({ k, l, I }) => (
      <button key={k} onClick={() => setMethod(k)} data-testid={`buy-method-${k}`}
        className={`flex items-center justify-center gap-2 rounded-xl border px-2 py-2.5 text-xs font-semibold transition-colors ${method === k ? "border-emerald-500 bg-emerald-500/10 text-emerald-400" : "border-[#212638] text-[#94A3B8] hover:bg-[#1C2030] hover:text-white"}`}>
        <I size={14} /> {l}
      </button>
    ))}
  </div>
);

export const BuyCard = ({ price, onDone }) => {
  const { user, setUser } = useAuth();
  const [method, setMethod] = useState("card");
  const [val, setVal] = useState("10");
  const [busy, setBusy] = useState(false);
  const usdBal = user?.usd_balance || 0;
  const amount = Math.round((parseFloat(val) || 0) * 100) / 100;
  const fee = feeOf(amount);
  const btc = price && amount > fee ? (amount - fee) / price : 0;
  const min = method === "card" ? MIN_CARD : 0.01;
  const max = method === "card" ? MAX : Math.min(MAX, usdBal);
  const error = amount <= 0 ? "" : amount < min ? `Minimum is ${fmtUsd(min)}` : amount <= MIN_FEE ? "Amount must be more than the $0.01 minimum fee" : amount > max ? (method === "card" ? `Maximum is ${fmtUsd(MAX)}` : "Insufficient USD balance") : "";

  const buy = async () => {
    setBusy(true);
    try {
      if (method === "card") {
        const { data } = await api.post("/payments/checkout", { usd_amount: amount, origin_url: window.location.origin });
        toast.success("Redirecting to secure card checkout...");
        window.location.href = data.checkout_url;
        return;
      }
      const { data } = await api.post("/trade/buy-balance", { usd_amount: amount });
      setUser(data.user);
      toast.success(`Bought ${fmtBtc(data.transaction.btc_amount)} BTC with your USD balance`);
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };

  return (
    <div className="space-y-5" data-testid="buy-card">
      <MethodToggle method={method} setMethod={setMethod} usdBal={usdBal} />
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#64748B]">You pay (USD)</p>
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 font-mono text-lg text-[#64748B]">$</span>
          <input value={val} onChange={(e) => setVal(e.target.value)} type="number" min="0" step="0.01" placeholder="0.00"
            data-testid="buy-usd-input"
            className="w-full rounded-xl border border-[#212638] bg-[#0F111A] py-3 pl-8 pr-4 font-mono text-lg outline-none transition-colors focus:border-[#F7931A]" />
        </div>
        <div className="mt-2 grid grid-cols-6 gap-2">
          {AMOUNTS.map((a) => (
            <button key={a} onClick={() => setVal(String(a))} data-testid={`buy-amount-${a}`}
              className={`rounded-lg border py-1.5 font-mono text-xs font-semibold transition-colors ${amount === a ? "border-[#F7931A] bg-[rgba(247,147,26,0.12)] text-[#F7931A]" : "border-[#212638] text-[#94A3B8] hover:bg-[#1C2030] hover:text-white"}`}>
              ${a}
            </button>
          ))}
        </div>
        {error && <p className="mt-2 text-xs text-red-400" data-testid="buy-amount-error">{error}</p>}
      </div>
      <div className="rounded-xl border border-[#212638] bg-[#0F111A] p-4 text-sm">
        <div className="flex justify-between"><span className="text-[#94A3B8]">You receive (est.)</span><span className="font-mono font-semibold text-[#F7931A]" data-testid="buy-btc-preview">{fmtBtc(btc)} BTC</span></div>
        <div className="mt-2 flex justify-between"><span className="text-[#94A3B8]">Rate</span><span className="font-mono">{price ? fmtUsd(price) : "—"}</span></div>
        <div className="mt-2 flex justify-between"><span className="text-[#94A3B8]">Fee (1%, min $0.01)</span><span className="font-mono" data-testid="buy-fee-preview">{fmtUsd(fee)}</span></div>
      </div>
      <button onClick={buy} disabled={busy || !price || amount <= 0 || !!error} data-testid="buy-submit-button"
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 py-3.5 font-semibold text-[#08090E] transition-colors hover:bg-emerald-400 disabled:opacity-50">
        {busy ? <Loader2 className="animate-spin" size={18} /> : method === "card" ? <CreditCard size={18} /> : <Wallet size={18} />}
        {method === "card" ? `Pay ${fmtUsd(amount)} with card` : `Buy instantly for ${fmtUsd(amount)}`}
      </button>
      <p className="flex items-center justify-center gap-1.5 text-xs text-[#64748B]"><ShieldCheck size={14} />
        {method === "card" ? "Secured by Stripe. Rate locked at checkout." : "Settles instantly from your USD balance."}</p>
    </div>
  );
};
